import { Play, Square } from 'lucide-solid';
import { createEffect, createMemo, createSignal, on, onCleanup, Show, untrack, type JSX } from 'solid-js';
import { BusyButton, EffectUnavailable } from '../components/ui';
import { selectedStartableAudioInput } from '../lib/audioInputs';
import { autoVJSets, type AutoVJLook } from '../lib/autoVJ';
import { lookPlayable, playLook } from '../lib/playLook';
import { operatorUnavailableReason, sameEffectLibrary, type EffectPackageItem } from '../lib/effectPackages';
import { relayVerdict, UNREPORTED_COMMAND_LIFETIME_MS, useVenue } from '../state/VenueProvider';
import { createAudioReactiveHold } from '../state/effectSettings';
import { AutoVJEditor } from './AutoVJEditor';
import type { TreeCardProps } from './treeCards';
import './AutoVJEffectPanel.css';
import { TEST_IDS } from '../testIds';

/**
 * The Auto VJ card: a tree-shaped card (S34). Its pane is Start / Stop and the room's sets; its tree —
 * sets › units › steps › layers — opens in the tree sheet (B84).
 */
export function AutoVJEffectPanel(props: TreeCardProps): JSX.Element {
  const venue = useVenue();
  const layout = createMemo(() => props.layout?.layout);
  /** Everything a step's layer may run: the installed packages, reactions among them. */
  const packages = createMemo<EffectPackageItem[]>((previous) => {
    const next = props.catalog();
    return previous && sameEffectLibrary(previous, next) ? previous : next;
  }, []);
  // Play puts a look on the room through the Audio reactive card's own settings and hold (B72).
  const audio = createAudioReactiveHold(venue, props.catalog);
  const direct = createMemo(() => venue.controllerState()?.effects?.find((effect) => effect.kind === 'auto_vj'));
  const mirrored = createMemo(() => venue.mirror()?.effect?.kind === 'auto_vj' ? venue.mirror()?.effect : undefined);
  const effectId = createMemo(() => String(direct()?.id ?? direct()?.effectId ?? mirrored()?.effectId ?? ''));
  // Start plays what the ROOM holds, so its availability reads the pushed library, never a draft.
  const activeSet = createMemo(() => autoVJSets(venue.autoVJLibrary())
    .find((set) => set.id === venue.autoVJLibrary().activeSetId));
  const startable = createMemo(() => {
    const units = activeSet()?.units ?? [];
    return units.some((unit) => (unit.steps ?? []).length > 0);
  });
  const controlsReason = createMemo(() => !venue.selectedDeviceId() ? 'Choose a controller.'
    : !venue.controlsEnabled() ? 'The controller is offline.' : '');

  // ---- Play: a look from this card held on the room until Stop (B72) -------------------------------
  const audioEffectId = audio.effectId;
  const playInput = createMemo(() => selectedStartableAudioInput(venue.audioInputs(), venue.selectedAudioInputIdentity()));
  /** Set once this card has put a look on the room; the room's running audio effect is then this card's to stop. */
  const [played, setPlayed] = createSignal(false);
  const playedEffectId = createMemo(() => played() ? audioEffectId() : '');
  // The audio effect stopping anywhere else ends this card's claim on it.
  createEffect(on(audioEffectId, (id, previous) => {
    if (previous && !id) setPlayed(false);
  }, { defer: true }));
  /**
   * A play the cloud never reported on is still claimed — the room may well have started the look — but
   * only for as long as the console holds any unreported command (UNREPORTED_COMMAND_LIFETIME_MS). If no
   * audio effect has appeared by then, the claim is dropped, so a later, unrelated start of the room's
   * audio effect is never adopted as this card's.
   */
  let unconfirmedPlay: ReturnType<typeof setTimeout> | undefined;
  function clearUnconfirmedPlay(): void {
    if (unconfirmedPlay !== undefined) clearTimeout(unconfirmedPlay);
    unconfirmedPlay = undefined;
  }
  onCleanup(clearUnconfirmedPlay);
  // The passing reasons Play waits on. Whether a role may Play at all decides whether it is shown
  // (the editor's `effects.run` gate), never whether it is greyed out.
  const canPlay = createMemo(() => venue.controlsEnabled()
    && Boolean(layout()) && Boolean(playInput()?.identity));

  async function play(look: AutoVJLook): Promise<void> {
    const loaded = layout();
    const input = playInput();
    if (!loaded || !input?.identity || !lookPlayable(look)) return;
    // Effects replace effects with no prompt (B62); the shared decision still guards a category change.
    if (!await venue.confirmTakeover({ source: 'effect', action: 'plays this look' })) return;
    let unreported = false;
    const done = await venue.runAction('play-look', async () => {
      try {
        await playLook({
          venue,
          call: (name, params) => venue.controllerCall(name, params),
          hold: audio.hold,
          layout: loaded,
          input: { ...input, identity: input.identity ?? '' },
          roomPath: venue.roomLayout(),
          audioRunning: Boolean(audioEffectId()),
        }, look);
      } catch (error) {
        // Only a refusal says the look did not reach the room. A relay timeout says nothing about the
        // room, so it may not take back the card's Stop for a look the room then really runs.
        if (relayVerdict(error) !== 'unreported') throw error;
        unreported = true;
      }
      return true;
    });
    // A refused re-Play leaves an earlier unconfirmed claim's timer running, so that claim still ends.
    if (!done) return;
    clearUnconfirmedPlay();
    setPlayed(true);
    if (unreported && !audioEffectId()) {
      unconfirmedPlay = setTimeout(() => {
        unconfirmedPlay = undefined;
        if (!audioEffectId()) setPlayed(false);
      }, UNREPORTED_COMMAND_LIFETIME_MS);
    }
  }

  async function start(): Promise<void> {
    if (!layout() || !startable()) return;
    if (!await venue.confirmTakeover({ source: 'effect', action: 'starts Auto VJ' })) return;
    await venue.runAction(
      'start-auto-vj',
      () => venue.controllerCall('start_auto_vj', {
        layout: layout(),
      }),
      'Auto VJ started',
    );
  }

  /**
   * Start from here (B73): run a set from one of its units onward, exactly as Auto VJ runs it — real
   * dwells and transitions, looping as normal — through the room's one takeover decision. The set is
   * named explicitly so a unit of the set open in the editor starts that set, active or not.
   */
  const canStartFrom = createMemo(() => venue.controlsEnabled() && Boolean(layout()) && props.item.available);

  async function startFrom(setId: string, unitId: string): Promise<void> {
    if (!layout()) return;
    if (!await venue.confirmTakeover({ source: 'effect', action: 'starts Auto VJ' })) return;
    await venue.runAction(
      'start-auto-vj',
      () => venue.controllerCall('start_auto_vj', {
        layout: layout(),
        setId,
        startUnitId: unitId,
      }),
      'Auto VJ started',
    );
  }

  /** The one Stop: the running set, or else the look this card is holding on the room. */
  const stopTarget = createMemo(() => effectId() || playedEffectId());

  async function stop(): Promise<void> {
    const id = stopTarget();
    if (!id) return;
    const stopped = await venue.runAction(
      'stop-effect',
      async () => {
        await venue.controllerCall('stop_effect', { effectId: id });
        return true;
      },
      'Effect stopped',
    );
    if (stopped && id === playedEffectId()) setPlayed(false);
  }

  createEffect(() => {
    if (!props.startRequested()) return;
    props.onStartHandled();
    untrack(() => void start());
  });

  return (
    <section class="surface effect-stage auto-vj-effect-stage" id="auto-vj-panel">
      <div class="section-heading"><h2>Auto VJ</h2></div>

      <Show when={!props.item.available && (startable() || !activeSet())}>
        <EffectUnavailable
          effectName="Auto VJ"
          reason={operatorUnavailableReason(props.item)}
          detail={props.unavailableDetail}
          testId={TEST_IDS.autoVjUnavailable}
        />
      </Show>

      {/* Start stays available while a played look holds the room; Stop stops whichever this card runs. */}
      <div class="auto-vj-transport">
        <Show when={!effectId()}>
          <Show when={startable()} fallback={(
            <Show when={activeSet()}>
              <EffectUnavailable
                effectName="Auto VJ"
                reason="Auto VJ set has nothing to play"
                testId={TEST_IDS.autoVjEmptySet}
              />
            </Show>
          )}>
            <Show
              when={layout() || props.loading}
              fallback={(
                <EffectUnavailable
                  effectName="Auto VJ"
                  detail={props.unavailableDetail}
                  testId={TEST_IDS.autoVjLayoutMissing}
                />
              )}
            >
              <BusyButton
                busyKey="start-auto-vj"
                class="button button-primary button-large"
                disabled={!layout() || !props.item.available || !venue.controlsEnabled()}
                onClick={() => void start()}
                data-testid={TEST_IDS.startAutoVj}
              ><Play size={18} /> Start</BusyButton>
            </Show>
          </Show>
        </Show>
        <Show when={stopTarget()}>
          <BusyButton
            busyKey="stop-effect"
            class="button button-danger button-large"
            disabled={!venue.controlsEnabled()}
            onClick={() => void stop()}
            data-testid={TEST_IDS.stopAutoVj}
          ><Square size={17} /> Stop</BusyButton>
        </Show>
      </div>
      <Show when={(startable() || effectId()) && props.item.available && controlsReason()}>
        <p class="form-error auto-vj-feedback" role="status">{controlsReason()}</p>
      </Show>
      <Show when={startable() && !layout() && props.loading && props.item.available}>
        <EffectUnavailable effectName="Auto VJ" testId={TEST_IDS.autoVjLayoutLoading} />
      </Show>

      <AutoVJEditor
        card={props.item}
        packages={packages()}
        canPlay={canPlay}
        onPlay={play}
        canStartFrom={canStartFrom}
        onStartFrom={startFrom}
      />
    </section>
  );
}
